import type { LayoutOptions } from '@base/shared';
import type { Shell } from '../../shell/choice.ts';

/**
 * Regras dos padrões de tela (ADR 0128, fase 4), sem React, para os testes. Cada peça de
 * `patterns/` lê daqui como se arrumar no layout escolhido.
 */

/**
 * Onde o item aberto de uma lista aparece:
 * - `split`: ao lado da lista (layout Mestre e detalhe);
 * - `inspector`: no painel da direita da moldura (layout Três colunas);
 * - `page`, `drawer`, `modal`: o ajuste "abrir um item" dos outros layouts.
 */
export type RecordMode = 'split' | 'inspector' | 'page' | 'drawer' | 'modal';

export function recordModeOf(shell: Shell | null, options: LayoutOptions | null): RecordMode {
  if (shell === 'split') return 'split';
  if (shell === 'inspector') return 'inspector';
  return options?.recordOpen ?? 'page';
}

/** Largura da coluna da lista no Mestre e detalhe (ajuste `listWidth`). */
export const LIST_WIDTHS: Record<LayoutOptions['listWidth'], string> = {
  narrow: 'md:grid-cols-[18rem_minmax(0,1fr)]',
  medium: 'md:grid-cols-[24rem_minmax(0,1fr)]',
  wide: 'md:grid-cols-[32rem_minmax(0,1fr)]',
};

/**
 * Agrupa os itens nas colunas de um quadro, na ordem das colunas. Item com coluna que não existe
 * não some: vai para a primeira, para ninguém perder um item de vista.
 */
export function groupByColumn<T>(
  items: readonly T[],
  columns: readonly string[],
  columnOf: (item: T) => string,
): Map<string, T[]> {
  const out = new Map(columns.map((c) => [c, [] as T[]]));
  const first = columns[0];
  for (const item of items) {
    const list = out.get(columnOf(item)) ?? (first !== undefined ? out.get(first) : undefined);
    list?.push(item);
  }
  return out;
}

export interface AgendaEvent {
  id: string;
  /** Dia da semana mostrado, de 0 (o primeiro dia da grade) a `days - 1`. */
  day: number;
  /** Início e fim em minutos desde a meia-noite. */
  start: number;
  end: number;
}

export interface PlacedEvent<E extends AgendaEvent> {
  event: E;
  /** Posição na grade do dia, em fração (0 a 1) da altura e da largura. */
  top: number;
  height: number;
  left: number;
  width: number;
}

/**
 * Posiciona os compromissos na grade da agenda (`from` a `to`, em minutos). Os que se encontram
 * no mesmo dia dividem a largura em faixas; o que fica fora do horário mostrado é cortado na borda;
 * o que não aparece nada (todo fora, ou fora dos dias) fica de fora.
 */
export function placeEvents<E extends AgendaEvent>(
  events: readonly E[],
  days: number,
  from: number,
  to: number,
): PlacedEvent<E>[] {
  const span = to - from;
  const out: PlacedEvent<E>[] = [];
  for (let day = 0; day < days; day++) {
    const today = events
      .filter((e) => e.day === day && e.end > from && e.start < to && e.end > e.start)
      .sort((a, b) => a.start - b.start || b.end - a.end);
    // Grupos de compromissos que se encontram; dentro de cada um, a primeira faixa livre.
    let group: { event: E; lane: number }[] = [];
    let groupEnd = -Infinity;
    const flush = () => {
      const lanes = Math.max(0, ...group.map((g) => g.lane)) + 1;
      for (const { event, lane } of group) {
        const start = Math.max(event.start, from);
        const end = Math.min(event.end, to);
        out.push({
          event,
          top: (start - from) / span,
          height: (end - start) / span,
          left: lane / lanes,
          width: 1 / lanes,
        });
      }
      group = [];
    };
    for (const event of today) {
      if (event.start >= groupEnd) {
        flush();
        groupEnd = -Infinity;
      }
      const busy = new Set(group.filter((g) => g.event.end > event.start).map((g) => g.lane));
      let lane = 0;
      while (busy.has(lane)) lane++;
      group.push({ event, lane });
      groupEnd = Math.max(groupEnd, event.end);
    }
    flush();
  }
  return out;
}

/**
 * Escala do gráfico: o topo "redondo" logo acima do maior valor e os traços do eixo, com passo
 * 1, 2 ou 5 vezes uma potência de 10 (nunca um eixo que termina em 937).
 */
export function niceScale(max: number, ticks = 4): { top: number; step: number; marks: number[] } {
  if (!(max > 0)) return { top: 1, step: 1, marks: [0, 1] };
  const raw = max / ticks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 5, 10].find((m) => m * pow >= raw) ?? 10) * pow;
  const top = Math.ceil(max / step - 1e-9) * step;
  const marks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) marks.push(Number(v.toPrecision(12)));
  return { top, step, marks };
}

/** Situação de cada etapa de um cadastro em etapas. */
export function stepStateOf(index: number, current: number): 'done' | 'current' | 'next' {
  return index < current ? 'done' : index === current ? 'current' : 'next';
}
